import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from 'utils/cn';
import Separator from '../Separator';


const DialogRoot = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-atarim={true}
    className={cn(
      'backdrop-blur-md data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 isolate z-10 bg-black/40',
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  showClose?: boolean;
  portal?: boolean;
  overlayProps?: React.ComponentPropsWithoutRef<typeof DialogOverlay>;
  container?: HTMLElement | ShadowRoot | null;
};

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showClose = true, portal = true, container = document.body, overlayProps, ...props }, ref) => {
  const Component = (
    <DialogPrimitive.Content
      data-atarim={true}
      ref={ref}
      className={cn(
        `data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed left-[50%] top-[50%] isolate z-10 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background px-4 pb-2 pt-3 shadow-lg duration-700 sm:rounded-2xl overflow-hidden`,
        className
      )}
      {...props}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        document.body.style.pointerEvents = '';
      }}
      onOpenAutoFocus={() => {
        document.body.style.pointerEvents = '';
      }}
    >
      {
        props.title &&
        <DialogPrimitive.Title>{props.title || ''} </DialogPrimitive.Title>
      }
      {children}
      {showClose && (
        <DialogClose
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="ring-offset-background focus:ring-ring absolute right-2 top-2 rounded-md bg-background p-2 text-red-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X size={18} strokeWidth={2.5} />
          <span className="sr-only">Close</span>
        </DialogClose>
      )}
    </DialogPrimitive.Content>
  )

  if (portal) {
    return (
      <DialogPortal container={container}>
        <DialogOverlay {...overlayProps} />
        {Component}
      </DialogPortal>
    )
  }

  return Component
});

DialogContent.displayName = DialogPrimitive.Content.displayName;

interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> { }

const DialogHeader: React.FC<DialogHeaderProps> = ({ className, ...props }) => {
  const { children } = props;
  return (
    <div
      className={cn(
        'flex flex-col min-h-10 pb-2 justify-center sm:text-left relative',
        className
      )}
      {...props}
    >{children}
      <Separator className='absolute bottom-0 left-0 dark:bg-border' />
    </div>
  )
};

DialogHeader.displayName = 'DialogHeader';

interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> { }

const DialogFooter: React.FC<DialogFooterProps> = ({ className, ...props }) => (
  <div
    className={cn(
      'flex flex-col-reverse px-4 pb-4 sm:flex-row sm:justify-end sm:space-x-2 text-textcolor',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'font-sans text-lg font-medium leading-none tracking-normal text-textcolor',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('px-6 py-2 text-sm text-textcolor', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// Create a namespace for the Dialog component
const Dialog = Object.assign(DialogRoot, {
  Trigger: DialogTrigger,
  Portal: DialogPortal,
  Close: DialogClose,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Header: DialogHeader,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
});

export default Dialog;
